import React, { useEffect, useState } from "react";
import { Form, Field, Formik } from "formik";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import {
  currentLanguage,
  handleCarPlateNum,
  handleCarPlateChar,
  limitizeValue
} from "../../util/common";
import Button from "./Button";
import { isEmpty } from "lodash";
import NotificationHelper from "./NotificationHelper";
import { UserService, ElmService, AppointmentService } from "../../services";
import { getFormattedCarNumberPlate, getSelectedCarPayload } from "../../util/carOptionsUtils";
import Select from "react-select";
import { useSelector } from "react-redux";
import { getSavedCarsOptions } from "../../util/selectOptions";
import { Link } from "react-router-dom";
import { useAppNavigation } from "../../hooks";

const VehicleDetailsStep = (props) => {
  const {
    setView,
    setShowLoader,
    globalValues,
    setGlobalValues,
    isUpdating,
    serviceId,
    isSubscription
  } = props;
  const lang = currentLanguage();

  const { t } = useTranslation();
  const user = useSelector((state) => state.user.details);
  const { localeRoutes } = useAppNavigation();
  const [savedCars, setSavedCars] = useState([]);
  const [selectedCar, setSelectedCar] = useState({});

  useEffect(() => {
    if (user.id && !isUpdating) getUserSavedCars();

    if (globalValues.istimaraSequenceNumber) {
      const carDetails = getSelectedCarPayload(globalValues);
      setSelectedCar(carDetails);
    }
  }, []);

  const isElmDataAvailable = !isEmpty(selectedCar);
  const savedCarOptions = getSavedCarsOptions(savedCars);

  const getUserSavedCars = async () => {
    const savedCarsResponse = await UserService.getUserCars({
      userId: user.id,
      serviceId,
      isSubscription
    });
    if (savedCarsResponse) setSavedCars(savedCarsResponse.userCars || []);
  };

  const handleSavedCarsChange = (e) => {
    const selectedSavedCar = savedCars.find((sc) => sc.carId === e.value);
    const carDetails = getSelectedCarPayload(selectedSavedCar);
    setSelectedCar(carDetails);
  };

  const getElmCarDetail = async (istimaraNo) => {
    setShowLoader(true);
    try {
      const response = await ElmService.getCarDetail(istimaraNo);
      if (response.vinNo) {
        const isValidCar = await verifyCarForService(response);
        if (!isValidCar) return;

        const carDetails = getSelectedCarPayload(response);
        setSelectedCar(carDetails);
      }
    } catch (err) {
      NotificationHelper.error(t("myAccount.carNotFoundOnIstimara"));
      console.error("error", err);
    } finally {
      setShowLoader(false);
    }
  };

  const verifyCarForService = async (car) => {
    let result = false;
    try {
      const { statusCode } = await AppointmentService.validateCarAgainstService({
        serviceId,
        make: car.brand.value_en,
        model: car.product.value_en,
        year: car.modelYear,
        isSubscription
      });

      if (statusCode === 200) result = true;
    } catch (err) {
      NotificationHelper.error(t("scheduleAppointment.carNotAssignedToService"));
      console.error("error", err);
      result = false;
    }
    return result;
  };

  const handleSubmit = async (values) => {
    if (!isElmDataAvailable) {
      const isExisingCarOnIstimara = savedCars.find(
        (sc) => sc.istimaraSequenceNumber === values.istimaraSequenceNumber
      );
      if (isExisingCarOnIstimara) {
        const carDetails = getSelectedCarPayload(isExisingCarOnIstimara);
        setSelectedCar(carDetails);
      } else await getElmCarDetail(values.istimaraSequenceNumber);

      return;
    }

    const doCarExistOnNumberPlate = savedCars.find(
      (sc) =>
        sc.carNumberPlate.toUpperCase() ===
        getFormattedCarNumberPlate(values.plateNumber + values.plateChar)
    );
    if (!values.savedCars && doCarExistOnNumberPlate)
      return NotificationHelper.error(t("myAccount.carAlreadyExistWithPlateNum"));

    const carDetails = getSelectedCarPayload({ ...selectedCar, ...values });
    setGlobalValues({ ...globalValues, ...carDetails });
    setView(2);
  };

  const vehicleDetailsValidations = () => {
    return Yup.object().shape({
      istimaraSequenceNumber: Yup.string()
        .required("estimationRequest.required")
        .matches(/^\d{8,10}$/, "scheduleAppointment.istimaraValidation"),

      ...(isElmDataAvailable && {
        plateNumber: Yup.string()
          .required("estimationRequest.required")
          .max(4, "estimationRequest.mustBeMax5Digits")
          .min(4, "estimationRequest.mustBeMin4Digits"),
        plateChar: Yup.string()
          .required("estimationRequest.required")
          .min(5, "estimationRequest.mustBeMin3Letters")
          .max(6, "estimationRequest.mustBeMax4Letters")
      })
    });
  };

  return (
    <>
      <Formik
        initialValues={{
          savedCars: selectedCar.carId || "",
          istimaraSequenceNumber: selectedCar.istimaraSequenceNumber || "",
          plateNumber: selectedCar.plateNumber || "",
          plateChar: selectedCar.plateChar || ""
        }}
        enableReinitialize
        validationSchema={vehicleDetailsValidations}
        onSubmit={handleSubmit}>
        {({ errors, touched }) => (
          <Form className="car-details">
            <div className="container">
              <h4 className="text-sm-center mt-5 mb-4">
                {t("estimationRequest.vehicleDetails")}
                <span className="required-flag">*</span>
              </h4>

              <div className="col-md-10 col-lg-8 col-xl-6 mx-auto elm-car-details">
                {savedCars.length ? (
                  <>
                    <div className="car-select">
                      <Select
                        name="savedCars"
                        classNamePrefix="select"
                        options={savedCarOptions}
                        value={savedCarOptions.find((opt) => opt.value === selectedCar.carId) || ""}
                        onChange={handleSavedCarsChange}
                        placeholder={t("myAccount.selectFromSavedCars")}
                      />
                    </div>

                    <div className="text-center my-3">
                      <b>{t("myAccount.savedCarOrIstimara")}</b>
                    </div>
                  </>
                ) : null}

                <div>
                  <Field
                    type="number"
                    id="istimaraSequenceNumber"
                    name="istimaraSequenceNumber"
                    placeholder={t("scheduleAppointment.istimaraSequenceNumber")}
                    className="form-control"
                    pattern="[0-9]*"
                    onInput={(e) => limitizeValue(e, 10)}
                    disabled={isElmDataAvailable}
                  />
                  {errors.istimaraSequenceNumber && touched.istimaraSequenceNumber && (
                    <span className="red-color">{t(errors.istimaraSequenceNumber)}</span>
                  )}
                </div>
                {isElmDataAvailable && (
                  <>
                    {!isUpdating && (
                      <div className="w-100 mt-2 text-end">
                        <Button type="yellow-text" onClick={() => setSelectedCar({})}>
                          {t("scheduleAppointment.editCarIstimara")}
                        </Button>
                      </div>
                    )}

                    <div className="show-details">
                      <div>
                        <span>{t("scheduleAppointment.selectBrand")} </span>
                        <span>{selectedCar.brand?.[lang]}</span>
                      </div>
                      <div>
                        <span>{t("scheduleAppointment.selectModel")} </span>
                        <span>{selectedCar.model?.[lang]}</span>
                      </div>
                      <div>
                        <span>{t("scheduleAppointment.selectYear")} </span>
                        <span>{selectedCar.year}</span>
                      </div>
                      {!isUpdating && (
                        <div>
                          <span>{t("scheduleAppointment.vinNo")} </span>
                          <span>{selectedCar.vinNo || "N/A"}</span>
                        </div>
                      )}
                    </div>
                    <div className="car-insight">
                      <div className="divider mb-0 w-100">
                        <div className="mx-0">
                          <Field
                            type="text"
                            id="plateNumber"
                            name="plateNumber"
                            className="form-control"
                            placeholder={t("estimationRequest.licensePlate")}
                            inputMode="numeric"
                            onInput={handleCarPlateNum}
                            disabled={isUpdating || selectedCar.carId}
                          />
                          {errors.plateNumber && touched.plateNumber && (
                            <span className="red-color">{t(errors.plateNumber)}</span>
                          )}
                        </div>
                        <div className="mx-0">
                          <Field
                            type="text"
                            id="plateChar"
                            name="plateChar"
                            placeholder={t("estimationRequest.egSSS")}
                            className="form-control"
                            disabled={isUpdating || selectedCar.carId}
                            onInput={handleCarPlateChar}
                          />
                          {errors.plateChar && touched.plateChar && (
                            <span className="red-color">{t(errors.plateChar)}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    {selectedCar.carId && !isUpdating && (
                      <p className="my-account-link mt-0" style={{ textAlign: "start" }}>
                        {t("scheduleAppointment.myAccountMsg")}{" "}
                        <Link to={localeRoutes.myAccount[lang]}>
                          {t("scheduleAppointment.myAccountLink")}
                        </Link>
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            <div className="d-flex justify-content-center">
              <button type="button" className="back-btn" onClick={() => setView(0)}>
                {t("estimationRequest.back")}
              </button>
              <input type="submit" className="mx-0" value={t("estimationRequest.nextTwo")} />
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default React.memo(VehicleDetailsStep);
